<script setup lang="ts">
import ApiHelper from "@/helpers/apiHelper.ts";
import axios from "axios";
import {onBeforeUnmount, onMounted, ref} from "vue";

interface top10 {
    nick: string,
    score: string
}

const top10 = ref<top10[]>(Array(10).fill({nick: '-', score: '-'}));
const errorGetTop10 = ref<String | null>(null);
const rankingSection = ref<HTMLElement | null>(null);
const isVisible = ref(false);
let observer: IntersectionObserver | null = null;

async function getTop10() {
    await axios.get(ApiHelper.getRanking())
        .then(response => {
            if (response.status == 200 && response.data.ranking) {
                if (response.data.ranking.length < 10) {
                    const emptyItems = Array(10 - response.data.ranking.length).fill({nick: '-', score: '-'});
                    top10.value = [...response.data.ranking, ...emptyItems];
                } else {
                    top10.value = response.data.ranking;
                }
            }

        }).catch(error => {
            console.log(error)
            if (error.response) {
                errorGetTop10.value = "Błąd podczas ładowania rankingu. Spróbuj ponownie później.";
            }

        });
}

getTop10();

onMounted(() => {
    if (!rankingSection.value) {
        return;
    }

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                isVisible.value = true;
                observer?.unobserve(entry.target);
            });
        },
        {
            threshold: 0.05,
            rootMargin: "0px 0px -10% 0px"
        }
    );

    observer.observe(rankingSection.value);
});

onBeforeUnmount(() => observer?.disconnect());

</script>

<template>
    <div id="ranking" ref="rankingSection" :class="{ 'is-visible': isVisible }" class="relative w-full py-16 lg:py-32 overflow-hidden bg-default-white">

        <h2 class="ranking-title text-[2.5rem] lg:text-6xl text-center pb-4 lg:pb-8 text-primary">RANKING</h2>
        <div class="w-28 lg:w-52 h-px bg-primary/60 mx-auto mb-14"></div>

        <div class="relative w-fit mx-auto">
            <div class="ranking-flag" v-show="errorGetTop10 == null"></div>
            <div class="w-full flex justify-center relative z-10">
                <div class="w-full max-w-xl lg:max-w-[900px] relative px-4 pb-8">
                    <table class="ranking-table w-full table-fixed border-collapse mx-auto"
                           v-if="!errorGetTop10">
                        <thead class="ranking-head">
                        <tr>
                            <th class="bg-primary text-white font-medium text-xl lg:text-2xl py-3" scope="col">Imię / Nick</th>
                            <th class="bg-primary text-white font-medium text-xl lg:text-2xl py-3" scope="col">Wynik</th>
                        </tr>
                        </thead>
                        <tbody class="text-center bg-default-white text-xl lg:text-2xl">
                            <tr v-for="(item, index) in top10" class="ranking-row" :style="{ '--row-delay': `${index * 0.06}s` }">
                                <th class="text-primary font-light py-3" scope="row">
                                    {{ item?.nick }}
                                </th>
                                <td class="text-default-red font-medium py-3">{{ item?.score }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="text-center text-xl text-primary px-2" v-else>{{ errorGetTop10 }}</div>
                </div>
            </div>
            <div class="ranking-bolid" v-show="errorGetTop10 == null"></div>
        </div>

    </div>
</template>


<style scoped>
.ranking-title {
    letter-spacing: 4px;
    opacity: 0;
    transform: translateY(16px);
    transition:
        letter-spacing 0.8s ease,
        transform 0.8s ease,
        opacity 0.8s ease;
}

#ranking {
    background: var(--color-default-white);
    min-height: 500px;
}

.ranking-head {
    opacity: 0;
    transform: translateY(-10px);
    transition: transform 0.5s ease 0.2s, opacity 0.5s ease 0.2s;
}

.ranking-row {
    opacity: 0;
    transform: translateX(-10px);
    transition:
        transform 0.4s ease var(--row-delay),
        opacity 0.4s ease var(--row-delay);
}

.ranking-table th,
.ranking-table td {
    border: 1px solid var(--color-primary);
}

.ranking-flag {
    position: absolute;
    top: -100px;
    left: 0;
    transform: translateX(calc(-186px - 60px)) rotate(-4deg);
    width: 434px;
    height: 376px;
    background-image: url(@/assets/images/ranking/flaga.webp);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 1;
    pointer-events: none;
    transition: transform 0.9s ease;
}

.ranking-bolid {
    position: absolute;
    bottom: -87px;
    right: -195px;
    width: 366px;
    height: 484px;
    opacity: 0;
    transform: translateX(60px);
    background-image: url(@/assets/images/ranking/boluid.webp);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 1;
    pointer-events: none;
    transition: transform 0.9s ease 0.1s, opacity 0.9s ease 0.1s;
}

#ranking.is-visible .ranking-title {
    letter-spacing: 10px;
    opacity: 1;
    transform: translateY(0);
}

#ranking.is-visible .ranking-flag {
    transform: translateX(-186px) rotate(0deg);
}

#ranking.is-visible .ranking-bolid {
    opacity: 1;
    transform: translateX(0);
}

#ranking.is-visible .ranking-head {
    opacity: 1;
    transform: translateY(0);
}

#ranking.is-visible .ranking-row {
    opacity: 1;
    transform: translateX(0);
}

@media screen and (max-width: 1023px) {
    .ranking-flag {
        left: -120px;
        transform: translateX(-60px) rotate(-4deg);
        width: 314px;
        height: 272px;
        top: -55px;
    }

    .ranking-bolid {
        right: -147px;
        width: 283px;
        height: 375px;
        bottom: -45px;
    }

    #ranking.is-visible .ranking-flag {
        transform: translateX(0) rotate(0deg);
    }
}

</style>
